import { Cross2Icon } from '@radix-ui/react-icons'
import { DeviceForensic, FileEvent, FileEventResponse, getEventTypeLabel } from '../../types/DeviceForensicTypes'
import { useEffect, useState } from 'react'
import { fetchEventsForFile } from '../../reducers/devices'
import { useAppDispatch } from '../../reducers/store'
import { DEFAULT_PAGE } from '../../strings/api-consts'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IconArrowUp } from '@tabler/icons-react'
import { Button, Tooltip } from '@radix-ui/themes'
import { formatUtcTimestamp } from '../../utils/dates'

interface IPopoutProps {
  setShowPopout: (show: boolean) => void
  popoutData: DeviceForensic
}

const DeviceForensicPopoutContent = (props: IPopoutProps) => {
  const di = useAppDispatch()
  const { setShowPopout, popoutData } = props
  const [fileHistory, setFileHistory] = useState<FileEvent[]>([])
  const [currentPageDeviceEvents, setCurrentPageDeviceEvents] = useState(DEFAULT_PAGE)
  const [totalCount, setTotalCount] = useState(0)
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    const scrollContainer = document.getElementById('scrollablePopout')
    if (!scrollContainer) return

    const handleScroll = () => {
      setShowScrollTop(scrollContainer.scrollTop > 200) // threshold in px
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    const scrollContainer = document.getElementById('scrollablePopout')
    if (!scrollContainer) return
    scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const fetchFileEvents = async (deviceId: string, filePath: string) => {
    const response = (await di(
      fetchEventsForFile(deviceId, filePath, currentPageDeviceEvents, 10)
    )) as FileEventResponse
    setFileHistory([...fileHistory, ...response.events])
    setTotalCount(response.totalCount)
  }

  useEffect(() => {
    if (popoutData.deviceIdentifier) {
      fetchFileEvents(popoutData.deviceIdentifier, popoutData.path)
    }
  }, [popoutData, currentPageDeviceEvents])

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="font-semibold border-mainBorder border-b flex px-6 py-3 bg-mainBackground text-lg">
          <div className="overflow-hidden whitespace-nowrap text-ellipsis ">{popoutData.shortPath}</div>
          <div className="flex justify-end items-center pr-2 flex-grow">
            <div
              className="cursor-pointer"
              onClick={() => {
                setShowPopout(false)
              }}
            >
              <Cross2Icon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 mb-8">
          <div className="leading-normal flex flex-col gap-2 px-8">
            <div className="font-semibold">Details</div>
          </div>
          <div className="leading-normal flex flex-col px-8 text-sm">
            <div className="font-semibold">Path</div>
            <div>{popoutData.path}</div>
          </div>

          {popoutData.deletedTimestamp && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">Deleted</div>
              <div>
                <Tooltip content={popoutData.deletedTimestamp} side="top" align="end" className="max-w-[240px]">
                  <span>
                    {popoutData.deleted ? formatUtcTimestamp(popoutData.deletedTimestamp, true, true) : ''}
                  </span>
                </Tooltip>
              </div>
            </div>
          )}

          {popoutData.firstSeen && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">First Seen</div>
              <div>
                <span>{popoutData.firstSeen ? formatUtcTimestamp(popoutData.firstSeen, true, true) : ''}</span>
              </div>
            </div>
          )}
          {popoutData.lastSeen && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">Last Seen</div>
              <div>
                <span>{popoutData.lastSeen ? formatUtcTimestamp(popoutData.lastSeen, true, true) : ''}</span>
              </div>
            </div>
          )}
          {popoutData.inodeVersions && popoutData.inodeVersions.length > 0 && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">Inode</div>
              <div>{popoutData.inodeVersions.join(', ')}</div>
            </div>
          )}
          {popoutData.created && popoutData.created !== null && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">Created</div>
              <span>{popoutData.created ? formatUtcTimestamp(popoutData.created, true, true) : ''}</span>
            </div>
          )}
          {popoutData.modified && popoutData.modified !== null && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">Modified</div>
              <span>{popoutData.modified ? formatUtcTimestamp(popoutData.modified, true, true) : ''}</span>
            </div>
          )}
          {popoutData.size !== undefined && popoutData.size !== null && popoutData.size >= 0 && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">Size</div>
              <div className="text-sm">{popoutData.size} bytes</div>
            </div>
          )}
          {popoutData.items !== undefined && popoutData.items !== null && popoutData.items >= 0 && (
            <div className="leading-normal flex flex-col px-8 text-sm">
              <div className="font-semibold">Items</div>
              <div>{popoutData.items}</div>
            </div>
          )}
          {popoutData.mounts && popoutData.mounts.length > 0 && (
            <div className="leading-normal flex flex-col gap-2 px-8">
              <hr className="my-2 border-neutral-4 dark:border-neutral-9" />
              <div className="font-semibold">Mounts</div>
              {popoutData.mounts.map((mount, index) => (
                <div key={index} className="text-sm flex flex-col gap-2">
                  <div>
                    <div className="font-semibold">Mounted From </div>
                    <div>{mount.mountedFrom}</div>
                  </div>
                  <div>
                    <div className="font-semibold">Files</div>
                    <div>{mount.files}</div>
                  </div>
                  <div>
                    <div className="font-semibold">Block Size</div>
                    <div>{mount.blockSize} bytes</div>
                  </div>
                  <div>
                    <div className="font-semibold">Mount Flags</div>
                    <div>{mount.mountFlags}</div>
                  </div>
                  <div>
                    <div className="font-semibold">Free Files</div>
                    <div>{mount.freeFiles}</div>
                  </div>
                  <div>
                    <div className="font-semibold">Available Bytes</div>
                    <div>{mount.availableBytes} bytes</div>
                  </div>
                  <div>
                    <div className="font-semibold">Free Bytes</div>
                    <div>{mount.freeBytes} bytes</div>
                  </div>
                  <div>
                    <div className="font-semibold">Total Bytes</div>
                    <div>{mount.totalBytes} bytes</div>
                  </div>
                  <div>
                    <div className="font-semibold">File System Type</div>
                    <div>{mount.fileSystemType}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {fileHistory && fileHistory.length > 0 && (
            <div className="leading-normal flex flex-col gap-2 px-8 h-full">
              <hr className="my-2 border-neutral-4 dark:border-neutral-9" />
              <div className="font-semibold">File History</div>
              <InfiniteScroll
                dataLength={fileHistory.length}
                next={() => setCurrentPageDeviceEvents((prev) => prev + 1)}
                hasMore={fileHistory.length < totalCount}
                loader={<h4>Loading...</h4>}
                scrollableTarget="scrollablePopout"
                className="flex flex-col gap-4"
              >
                <div className="flex gap-8 flex-col">
                  {fileHistory.map((event, index) => (
                    <div key={index} className="text-sm flex flex-col gap-2">
                      <div>
                        <div className="font-semibold">Event Type</div>
                        <div>{getEventTypeLabel(event.eventType)}</div>
                      </div>
                      <div>
                        <div className="font-semibold">Event Time</div>
                        <span>{formatUtcTimestamp(event.timestamp, true, true)}</span>
                      </div>
                      {event.inode && (
                        <div>
                          <div className="font-semibold">Inode</div> <div>{event.inode}</div>
                        </div>
                      )}
                      <div>
                        <div className="font-semibold">Event Details </div> <div>{event.eventDetails}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </InfiniteScroll>
              {fileHistory.length < totalCount && (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPageDeviceEvents((prev) => prev + 1)}
                    className="outline-1 dark:border-secondaryPurple dark:text-secondaryPurple dark:shadow-none dark:outline"
                  >
                    Load More
                  </Button>
                </div>
              )}
              {showScrollTop && (
                <button
                  onClick={scrollToTop}
                  className="fixed bottom-6 right-6 p-3 rounded-full bg-white/80 dark:bg-lightViolet shadow-lg hover:bg-white hover:shadow-xl transition-all backdrop-blur-sm"
                >
                  <IconArrowUp className="w-5 h-5 text-gray-800 dark:text-gray-200" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
export default DeviceForensicPopoutContent
